import React from 'react';
import { Music, Users, BarChart3, Sparkles } from 'lucide-react';
import { Button } from '../UI/Button';
import { useTranslation } from 'react-i18next';
import { getLanguagePathPrefix } from '../../i18n';
import { KombyLogo } from '../Branding/KombyLogo';
import { useTheme } from '../../contexts/ThemeContext';
import { getHeroSideImage, getHeroVideoSrc } from '../../config/heroMedia';
import { useDeferredHeroVideo } from '../../hooks/useDeferredHeroVideo';

export const HeroNightclub: React.FC = () => {
  const { t } = useTranslation(['common', 'home']);
  const { isBlack } = useTheme();
  const playVideo = useDeferredHeroVideo(!isBlack);

  const title = t('home:nightclub.seo.h1', {
    defaultValue: t('home:nightclub.hero.title'),
  });
  const parts = title.split(' ');
  const nightWords = [
    'night', 'nuit', 'noche', 'nacht', 'notte', 'noite', 'noc',
    'ночь', 'ніч', 'éjszaka', 'noapte', 'нощ', 'noć', 'noč',
    'νύχτα', 'gece', 'natt', 'nat', 'yö', 'öö', 'nakts', 'naktis',
    '夜', '밤', 'đêm', 'malam', 'الليل', 'לילה', 'شب', 'रात', 'กลางคืน',
  ];
  const nightIndex = parts.findIndex((p) =>
    nightWords.some((word) => p.toLowerCase() === word.toLowerCase()),
  );

  return (
    <section className="py-20 relative overflow-hidden">
      {!isBlack && playVideo && (
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src={getHeroVideoSrc('nightclub')} type="video/mp4" />
        </video>
      )}
      <div className={`absolute inset-0 z-[1] ${isBlack ? 'bg-transparent' : 'bg-black/60 dark:bg-black/70'}`} />

      <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center p-3 bg-purple-100/90 dark:bg-purple-900/50 rounded-xl h-14 w-14 backdrop-blur-sm">
                <Music className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex items-center h-14">
                <KombyLogo className="h-14 w-auto" animate={false} />
              </div>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 drop-shadow-lg">
              {nightIndex !== -1 ? (
                <>
                  <span className="text-purple-400">{parts.slice(0, nightIndex + 1).join(' ')}</span>{' '}
                  {parts.slice(nightIndex + 1).join(' ')}
                </>
              ) : (
                title
              )}
            </h1>

            <p className="text-xl text-white/90 mb-8 leading-relaxed drop-shadow-md">
              {t('home:nightclub.hero.subtitle')}
            </p>

            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              {t('home:nightclub.hero.description')}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="flex items-center space-x-3">
                <Sparkles className="h-5 w-5 text-purple-400" />
                <span className="text-white/90">{t('home:nightclub.hero.stats.vipReservations')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <BarChart3 className="h-5 w-5 text-purple-400" />
                <span className="text-white/90">{t('home:nightclub.hero.stats.realTimeInsights')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-purple-400" />
                <span className="text-white/90">{t('home:nightclub.hero.stats.trustedWorldwide')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Music className="h-5 w-5 text-purple-400" />
                <span className="text-white/90">{t('home:nightclub.hero.stats.hongKongToLondon')}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button variant="primary" size="lg" onClick={() => {
                window.location.href = `${getLanguagePathPrefix()}/pricing`;
              }}>
                {t('home:nightclub.hero.cta.primary')}
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="text-white border-white hover:bg-white/10"
                onClick={() => {
                  window.location.href = `${getLanguagePathPrefix()}/contact/`;
                }}
              >
                {t('common:cta.viewDemo')}
              </Button>
            </div>
          </div>

          <div className="relative hidden lg:block">
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={getHeroSideImage('nightclub')}
                alt={t('home:nightclub.hero.imageAlt')}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                width={800}
                height={384}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/40 to-transparent" />

              <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">10K+</div>
                  <div className="text-sm text-gray-600">{t('home:nightclub.hero.floatingStats.vipTables')}</div>
                </div>
              </div>

              <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">24/7</div>
                  <div className="text-sm text-gray-600">{t('home:nightclub.hero.floatingStats.support')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
